import { JobPosition } from '@restworld/utility-types';
import { getCurrencySymbol } from './currency';
import { isItalianVenue } from './venue';

/**
 * Single, centralized salary formatter for a job position.
 *
 * Takes the raw salary fields exactly as they come from the backend (salary /
 * min_salary / max_salary / salary_type / salary_currency / salary_month_number
 * / payment_type_var) and returns the one display string used across every
 * surface: job detail page, map popup, explorer cards. Keeping a single
 * function avoids the previous drift between the detail formatter and the card
 * preview formatter.
 *
 * Output shape: amount + currency symbol + net/gross ("netti"/"lordi") + period
 * ("mensili"/"all'ora"/"a servizio"). Annual gross figures (RAL) are shown in
 * compact "k" notation with a "RAL" label ("€56.5k RAL", "€24k - 27k RAL") so
 * they can't be misread as monthly pay. Returns null when the position carries
 * no salary, so callers can decide their own fallback (e.g. "Stipendio da
 * concordare").
 *
 * ──────────────────────────────────────────────────────────────────────────
 * ⚠️  CROSS-REPO SYNC — this function has a TWIN in the other repository:
 *       • restworld-frontend (here): libs/utils-common/src/utils/salary.ts → formatJobPositionSalary
 *       • website:                   src/lib/cerco-lavoro/salary.ts        → formatSalary
 *     Same algorithm; only the field casing differs (the website maps backend
 *     fields to camelCase). User-facing words are injected via a `SalaryLabels`
 *     object built from i18n (never hardcoded), so keep the label set in sync in
 *     both repos too. If you change ANY salary-display rule here (k-notation,
 *     RAL / lordi / netti labels, payment period, currency), make the IDENTICAL
 *     change in the other repo's twin in the same change-set — otherwise the
 *     live worker app and the SEO pages drift apart.
 *
 *     ONE intentional difference (see the "payment period" block below): here
 *     the monthly period is suppressed when `salary_month_number` is set,
 *     because the worker-app card shows the instalment count in a separate
 *     "per N mensilità" label. The website twin has no such label: it treats
 *     monthly as the implicit default (no "mensili") and only appends a
 *     non-standard instalment count ("· 13 mens.", omitted for the standard 12).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** Compact "k" notation: 24000 → "24k", 56500 → "56.5k". */
const toK = (value: number): string => {
  const thousands = Math.round((value / 1000) * 10) / 10;
  return `${Number.isInteger(thousands) ? thousands.toFixed(0) : thousands.toFixed(1)}k`;
};

/**
 * Italian number with the thousands separator forced ("1.700", "56.500").
 * Manual grouping (dot every 3 digits) instead of `Intl.NumberFormat` with
 * `useGrouping: 'always'`: the default it-IT CLDR rule omits the separator for
 * 4-digit numbers ("1700"), and this repo's TS lib doesn't type the 'always'
 * option. Output matches the website twin's `formatNumberIt`.
 */
const formatThousands = (value: number): string =>
  Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Compact amount for space-constrained previews (e.g. the explorer map pin
 * sub-label): abbreviates thousands to "k" notation ("20000" → "20k", "1800" →
 * "1.8k") but echoes values under 1000 back verbatim ("9", "9.5", "150"), so
 * hourly and per-service figures don't collapse to a misleading "0k" and keep
 * any decimals (an hourly "9.5" must not round to "10"). The abbreviation is
 * gated by magnitude, not payment type, which in practice compacts only the
 * monthly and annual amounts that are actually large.
 *
 * Accepts the raw string or number as it arrives in the marker payload; returns
 * the input coerced to a string unchanged when it isn't a finite number.
 *
 * Note: this reuses the same `toK` helper as `formatJobPositionSalary`, but does
 * NOT reproduce its full display (no currency, no net/gross, no RAL/period
 * label) — the pin renders those separately. It has no website twin: the SEO
 * pages have no map.
 */
export const formatSalaryAmountCompact = (value: number | string): string => {
  // Guard the `Number('') === 0` / `Number('  ') === 0` footgun: an empty or
  // blank string is not a number, so echo it back rather than rendering "0".
  if (typeof value === 'string' && value.trim() === '') return value;
  const amount = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(amount)) return `${value}`;
  // Only large amounts are abbreviated; smaller ones are returned as-is so
  // hourly/per-service decimals survive (`formatThousands` would round them).
  return Math.abs(amount) >= 1000 ? toK(amount) : `${value}`;
};

/**
 * Localized words used to build the salary string. Injected by the caller from
 * the i18n catalogue, because this is a pure function (not a React component)
 * and must not hardcode user-facing copy.
 */
export type SalaryLabels = {
  /** "netti" */
  net: string;
  /** "lordi" */
  gross: string;
  /** "RAL" — annual gross. */
  ral: string;
  /** "mensili" */
  monthly: string;
  /** "all'ora" */
  hourly: string;
  /** "a servizio" */
  perService: string;
  /** "Partirà da" */
  from: string;
  /** "Fino a" */
  upTo: string;
};

/**
 * Builds the `SalaryLabels` from a translation function (`t`) scoped to the
 * `common` namespace. Keep the key names in sync with the locale catalogues
 * under each app's `public/locales/{it,en}/common.json`.
 */
export const salaryLabelsFromT = (t: (key: string) => string): SalaryLabels => ({
  net: t('salary_net'),
  gross: t('salary_gross'),
  ral: t('salary_ral'),
  monthly: t('salary_monthly'),
  hourly: t('salary_hourly'),
  perService: t('salary_per_service'),
  from: t('salary_from'),
  upTo: t('salary_up_to'),
});

export const formatJobPositionSalary = (jobPosition: Partial<JobPosition>, labels: SalaryLabels): string | null => {
  const { salary, min_salary, max_salary, salary_type, salary_currency, salary_month_number, payment_type_var } =
    jobPosition || {};

  if (salary == null && min_salary == null && max_salary == null) return null;

  const currency = getCurrencySymbol(salary_currency);

  // "k" notation only for annual gross (RAL); monthly/hourly stay as full
  // numbers with the Italian thousands separator ("1.700").
  const isAnnual = payment_type_var?.key === 'annual' && salary_type === 'gross';
  const renderAmount = (value: number): string => (isAnnual ? toK(value) : formatThousands(value));

  let salaryString = '';

  const isMinSalaryOnlyPresent = min_salary != null && max_salary == null;
  const isMaxSalaryOnlyPresent = max_salary != null && min_salary == null;

  // Currency always before the amount: "€1.700", "Partirà da €1.700", "€56.5k".
  if (isMinSalaryOnlyPresent) salaryString = `${labels.from} ${currency}${renderAmount(min_salary)}`;
  else if (isMaxSalaryOnlyPresent) salaryString = `${labels.upTo} ${currency}${renderAmount(max_salary)}`;
  else {
    const salaryAmt =
      min_salary != null && max_salary != null
        ? `${renderAmount(min_salary)} - ${renderAmount(max_salary)}`
        : salary != null
        ? renderAmount(salary)
        : '';
    salaryString = `${currency}${salaryAmt}`;
  }

  // Net / gross, or "RAL" for an annual gross figure (Retribuzione Annua Lorda).
  const salaryLabel = isAnnual ? labels.ral : salary_type === 'gross' ? labels.gross : labels.net;
  salaryString = salaryString.concat(` ${salaryLabel}`);

  // Payment period. Skipped for RAL ("annual" is already implied by the label),
  // and only when there is no explicit number of monthly instalments. The
  // 'annual' payment type never reaches here (it's handled as RAL above), so the
  // switch only covers hourly / per-service / monthly.
  if (!isAnnual && payment_type_var && !salary_month_number) {
    let paymentTypeLabel = '';
    switch (payment_type_var.key) {
      case 'hour':
        paymentTypeLabel = labels.hourly;
        break;
      case 'service':
        paymentTypeLabel = labels.perService;
        break;
      default:
        paymentTypeLabel = labels.monthly;
    }
    salaryString = salaryString.concat(` ${paymentTypeLabel}`);
  }

  return salaryString;
};

/**
 * Whether the card's "per N mensilità" instalment label should be shown for a
 * job position.
 *
 * Mensilità (the 12/13/14 split) is an Italian-CCNL concept and IS useful info to
 * surface, RAL included: some employers fold the 13th/14th into 12 instalments,
 * so knowing the count is what makes a net estimate realistic (ambassador note).
 * It is shown for any Italian job that carries an instalment count, and dropped
 * only for FOREIGN jobs (e.g. Germany, which has no 13th/14th month) — that was
 * the original bogus "per 14 mensilità" the ambassadors flagged.
 *
 * `salary_month_number` may be null/absent (hourly, per-service, or just not set),
 * in which case there is no chip to show. Callers should pass it to the card ONLY
 * when this returns true.
 */
export const shouldShowMensilita = (jobPosition: Partial<JobPosition>): boolean => {
  const { salary_month_number, restaurant } = jobPosition || {};
  return !!salary_month_number && isItalianVenue(restaurant?.country);
};
